import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { certificatesAPI } from '../../api';
import Layout from '../../components/Layout';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../components/ui/Accordion';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { FormLabel } from '../../components/ui/FormLabel';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent } from '../../components/ui/Card';
import {
  FileCheck2,
  ClipboardList,
  Wrench,
  Lightbulb,
  Flame,
  PlugZap,
  Camera,
  Eye,
  Edit,
  Archive,
  ArchiveRestore,
  Trash2,
} from 'lucide-react';

const Certificates = () => {
  const navigate = useNavigate();
  
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [archivedFilter, setArchivedFilter] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    fetchCertificates();
  }, [pagination.page, search, typeFilter, archivedFilter]);

  const fetchCertificates = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await certificatesAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search,
        type: typeFilter || undefined,
        isArchived: archivedFilter,
      });
      
      setCertificates(response.certificates || []);
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          ...response.pagination
        }));
      }
    } catch (err) {
      setError(err.message || 'Failed to load certificates');
      console.error('Error fetching certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCertificate = async (certificateId) => {
    if (!confirm('Are you sure you want to delete this certificate?')) return;

    try {
      await certificatesAPI.delete(certificateId);
      fetchCertificates();
    } catch (err) {
      alert(err.message || 'Failed to delete certificate');
      console.error('Delete error:', err);
    }
  };

  const handleArchiveCertificate = async (certificateId, isArchived) => {
    try {
      await certificatesAPI.archive(certificateId, !isArchived);
      fetchCertificates();
    } catch (err) {
      alert(err.message || 'Failed to update certificate');
      console.error('Archive error:', err);
    }
  };

  const getStatusBadgeVariant = (status) => {
    const variants = {
      draft: 'neutral',
      issued: 'success',
      archived: 'warning',
    };
    return variants[status] || 'neutral';
  };

  const getTypeDisplayName = (type) => {
    const names = {
      eic: 'EIC',
      eicr: 'EICR',
      mewc: 'MEWC',
    };
    return names[type] || type.toUpperCase();
  };

  const handleCertificateClick = (certificate) => {
    // Route certificates to their specialized components
    if (certificate.certificateType === 'eicr') {
      navigate(`/tenant/certification/newEicr/${certificate.id}`);
    } else if (certificate.certificateType === 'eic') {
      navigate(`/tenant/certification/newEic/${certificate.id}`);
    } else if (certificate.certificateType === 'mewc') {
      navigate(`/tenant/certification/newMewc/${certificate.id}`);
    } else {
      navigate(`/tenant/certification/${certificate.id}`);
    }
  };

  const getClientDisplayName = (client) => {
    if (!client) return 'Unknown Client';

    const clientType = client.client_type || client.clientType;
    const companyName = client.company_name || client.companyName;
    const firstName = client.first_name || client.firstName;
    const lastName = client.last_name || client.lastName;
    const fallbackName = client.name || client.displayName;

    if (clientType === 'company') {
      return companyName || fallbackName || 'Unnamed Company';
    }

    const individualName = `${firstName || ''} ${lastName || ''}`.trim();
    if (individualName) {
      return individualName;
    }

    if (companyName) {
      return companyName;
    }

    return fallbackName || 'Unnamed Individual';
  };

  return (
    <TooltipProvider>
      <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Certificates
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage BS7671 electrical certificates (EIC, EICR, MEWC)
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowNewModal(true)}
              >
                + New Certificate
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <FormLabel htmlFor="search-input">Search</FormLabel>
              <Input
                id="search-input"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Search by certificate number, job reference, or client..."
                className="h-8"
              />
            </div>
            <div>
              <FormLabel htmlFor="type-filter">Type</FormLabel>
              <Select
                id="type-filter"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="">All Types</option>
                <option value="eic">EIC</option>
                <option value="eicr">EICR</option>
                <option value="mewc">MEWC</option>
              </Select>
            </div>
            <div>
              <FormLabel htmlFor="view-filter">View</FormLabel>
              <Select
                id="view-filter"
                value={archivedFilter ? 'archived' : 'active'}
                onChange={(e) => {
                  setArchivedFilter(e.target.value === 'archived');
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </Select>
            </div>
          </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {/* Certificate List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
            <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading certificates...</p>
          </div>
        ) : certificates.length === 0 ? (
          <Card className="text-center py-12">
            <p className="text-xs text-[var(--ds-text-muted)]">No certificates found</p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowNewModal(true)}
              className="mt-4"
            >
              Create Your First Certificate
            </Button>
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-[var(--ds-border-divider)]">
                {certificates.map((certificate) => (
                  <li key={certificate.id}>
                    <div className="px-4 py-4 sm:px-6 hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center">
                            <button
                              onClick={() => handleCertificateClick(certificate)}
                              className="text-h2 font-medium text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              {certificate.certificateNum}
                            </button>
                            <Badge variant="neutral" className="ml-3">
                              {getTypeDisplayName(certificate.certificateType)}
                            </Badge>
                            <Badge variant={getStatusBadgeVariant(certificate.status)} className="ml-2">
                              {certificate.status.charAt(0).toUpperCase() + certificate.status.slice(1)}
                            </Badge>
                          </div>
                          <div className="mt-2 text-xs text-[var(--ds-text-muted)]">
                            <span className="font-medium text-[var(--ds-text-primary)]">
                              {getClientDisplayName(certificate.client)}
                            </span>
                            {certificate.jobReference && (
                              <> · {certificate.jobReference}</>
                            )}
                            {certificate.dateIssued && (
                              <> · Issued {new Date(certificate.dateIssued).toLocaleDateString('en-GB')}</>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Tooltip content="View (coming soon)">
                            <Button
                              variant="icon"
                              size="sm"
                              disabled
                              className="text-[var(--ds-text-disabled)] cursor-not-allowed"
                              aria-label="View certificate (coming soon)"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleCertificateClick(certificate)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content={certificate.isArchived ? 'Unarchive' : 'Archive'}>
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleArchiveCertificate(certificate.id, certificate.isArchived)}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              {certificate.isArchived ? (
                                <ArchiveRestore className="h-4 w-4" />
                              ) : (
                                <Archive className="h-4 w-4" />
                              )}
                            </Button>
                          </Tooltip>
                          <Tooltip content="Delete">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleDeleteCertificate(certificate.id)}
                              className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Card>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <Card className="mt-6">
                <CardContent className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[var(--ds-text-secondary)]">
                    Showing page {pagination.page || 1} of {pagination.totalPages || 1} ({pagination.total || 0} total)
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page >= pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* New Certificate Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowNewModal(false)}></div>
          <div className="relative w-full max-w-5xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-h2 font-bold text-gray-900 dark:text-gray-100">Create Certificate</h2>
                <p className="text-meta text-gray-500 dark:text-gray-400">Choose a certificate type from the categories below</p>
              </div>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 md:p-6 max-h-[80vh] overflow-y-auto">
              <Accordion type="single" collapsible defaultValue="electrical-installations">
                <AccordionItem value="electrical-installations">
                  <AccordionTrigger>Electrical Installations</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <CertificateTile
                        Icon={FileCheck2}
                        iconRing="from-blue-500 to-cyan-500"
                        title="EIC"
                        description="Electrical Installation Certificate"
                        buttonGradient="from-blue-600 to-blue-500"
                        onClick={() => navigate('/tenant/certification/newEic')}
                        buttonText="Create EIC"
                      />
                      <CertificateTile
                        Icon={ClipboardList}
                        iconRing="from-purple-600 to-fuchsia-600"
                        title="EICR"
                        description="Electrical Installation Condition Report"
                        buttonGradient="from-purple-600 to-fuchsia-600"
                        onClick={() => navigate('/tenant/certification/newEicr')}
                        buttonText="Create EICR"
                      />
                      <CertificateTile
                        Icon={Wrench}
                        iconRing="from-teal-600 to-emerald-600"
                        title="MEWC"
                        description="Minor Electrical Works Certificate"
                        buttonGradient="from-teal-600 to-emerald-600"
                        onClick={() => navigate('/tenant/certification/newMewc')}
                        buttonText="Create MEWC"
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="emergency-lighting">
                  <AccordionTrigger>Emergency Lighting</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <DisabledCard title="EL Compliance" Icon={Lightbulb} />
                      <DisabledCard title="EL Small Install" Icon={Lightbulb} />
                      <DisabledCard title="EL Large Install" Icon={Lightbulb} />
                      <DisabledCard title="EL Periodic" Icon={Lightbulb} />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="fire">
                  <AccordionTrigger>Fire</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <DisabledCard title="Fire Certificates" Icon={Flame} />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="appliance-temp">
                  <AccordionTrigger>Appliance and Temporary Installations</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <DisabledCard title="ITEE (PAT)" Icon={PlugZap} />
                      <DisabledCard title="BS7907 Temporary Systems" Icon={PlugZap} />
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="alarm-cctv">
                  <AccordionTrigger>Alarm & CCTV</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <DisabledCard title="Alarm/CCTV" Icon={Camera} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      )}
    </Layout>
    </TooltipProvider>
  );
};

export default Certificates;

// Simple disabled card used for not-yet-available certificate types
const DisabledCard = ({ title, Icon = FileCheck2 }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 opacity-90">
    <div className="flex flex-col items-center text-center">
      <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-slate-500 to-slate-400 text-white shadow-sm ring-4 ring-slate-100 dark:ring-slate-700">
        <Icon className="h-6 w-6" />
      </span>
      <h3 className="mt-3 text-h2 font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="mt-1 text-meta text-gray-500 dark:text-gray-400">Coming soon</p>
    </div>
    <button
      disabled
      className="mt-4 w-full px-3 py-2 rounded-md bg-gradient-to-r from-slate-600 to-slate-500 text-white text-xs shadow-sm hover:from-slate-500 hover:to-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 active:translate-y-[1px] transition-all disabled:opacity-80 disabled:cursor-not-allowed"
    >
      Not Available Yet
    </button>
  </div>
);

// Reusable centered tile with icon and gradient button
const CertificateTile = ({ Icon, iconRing, title, description, onClick, buttonText, buttonGradient }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 flex flex-col items-center text-center">
    <span className={`inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br ${iconRing} text-white shadow-sm ring-4 ring-blue-100 dark:ring-white/10`}>
      <Icon className="h-6 w-6" />
    </span>
    <h3 className="mt-3 text-h2 font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
    {description && (
      <p className="mt-1 text-meta text-gray-600 dark:text-gray-300 max-w-[22rem]">{description}</p>
    )}
    <div className="mt-5 w-full">
      <button
        onClick={onClick}
        className={`w-full px-4 py-2 rounded-md bg-gradient-to-r ${buttonGradient} text-white text-xs shadow-sm hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-blue-500 active:translate-y-[1px] transition-all`}
      >
        {buttonText}
      </button>
    </div>
  </div>
);

